import { useState, useEffect } from 'react';
import { 
  User, 
  Plus, 
  Search, 
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Shield,
  Mail,
  Calendar,
  Building2,
  KeyRound,
  RefreshCw
} from 'lucide-react';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Input } from '@/components/common/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/common/Dialog';
import { Label } from '@/components/common/Label';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import PermissionMatrix from './PermissionMatrix';
import { EditablePermissionMatrix } from './EditablePermissionMatrix';
import { Role, Module, Permission } from '@/config/permissions';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { useAuthStore } from '@/hooks/useAuthStore';

interface CustomPermission {
  module: Module;
  permissions: Permission[];
}

interface UserData {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  department: string;
  lastLogin: string;
  createdAt: string;
  customPermissions?: CustomPermission[]; // Custom permissions override
}

import { ROLE_CONFIG as RBAC_ROLE_CONFIG, PERMISSIONS as RBAC_PERMISSIONS } from '@/config/permissions';

const ROLE_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  ADMIN: { label: 'Administrator', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Shield },
  ADMINISTRATOR: { label: 'Administrator', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Shield },
  ABM_MANAGER: { label: 'ABM Manager', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: User },
  ABM_OPS: { label: 'ABM Operations', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: User },
  ABM_OPERATIONS: { label: 'ABM Operations', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: User },
  MDA: { label: 'MDA', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: User },
  SALES: { label: 'Sales', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: User },
  USER: { label: 'User', color: 'bg-gray-100 text-gray-800 border-gray-200', icon: User },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Active', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  INACTIVE: { label: 'Inactive', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  SUSPENDED: { label: 'Suspended', color: 'bg-red-100 text-red-800 border-red-200' },
};

// API base — must match the rest of the app (apiClient). Relative '/api/...'
// would hit the portal origin, not the backend, and fail in production.
const API_BASE = import.meta.env.VITE_API_URL ?? '';

// Seed a custom-permission matrix from a role's defaults, so enabling "Custom"
// starts from the user's CURRENT access instead of an empty (deny-all) matrix.
// Without this, setting one module silently strips every other role default.
const roleDefaultsToCustom = (role: string): CustomPermission[] => {
  const matrix: Record<string, string[]> = (RBAC_PERMISSIONS as any)[role] || {};
  return Object.entries(matrix)
    .filter(([, perms]) => Array.isArray(perms) && perms.length > 0)
    .map(([module, perms]) => ({ module, permissions: [...perms] })) as CustomPermission[];
};

export default function UserManagementPage() {
  const { addToast } = useToast();
  const currentUser = useAuthStore((s) => s.user);
  const canResetPasswords = !!currentUser && ['ADMIN', 'ADMINISTRATOR', 'ABM_MANAGER'].includes(currentUser.role);

  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<'all' | Role>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED'>('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [useCustomPermissions, setUseCustomPermissions] = useState(false);
  const [customPermissions, setCustomPermissions] = useState<CustomPermission[]>([]);

  // Password reset dialog state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetUser, setResetUser] = useState<UserData | null>(null);
  const [resetMode, setResetMode] = useState<'auto' | 'manual'>('auto');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'ABM_OPERATIONS' as UserData['role'],
    status: 'ACTIVE' as UserData['status'],
    department: '',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, filterRole, filterStatus]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to load users',
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.department.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => user.status === filterStatus);
    }

    setFilteredUsers(filtered);
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Failed to create user');
      setShowCreateDialog(false);
      resetForm();
      await loadUsers();
      addToast({
        type: 'success',
        title: 'Success',
        description: 'User created successfully',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to create user',
      });
    }
  };

  const handleEdit = async () => {
    if (!selectedUser) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/api/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          // Send the array to set overrides, or null to clear them (revert to role defaults).
          customPermissions: useCustomPermissions ? customPermissions : null
        })
      });
      if (!response.ok) throw new Error('Failed to update user');
      setShowEditDialog(false);
      setSelectedUser(null);
      resetForm();
      setUseCustomPermissions(false);
      setCustomPermissions([]);
      await loadUsers();
      addToast({
        type: 'success',
        title: 'Success',
        description: useCustomPermissions
          ? 'User updated with custom permissions'
          : 'User updated successfully',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to update user',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/api/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete user');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      await loadUsers();
      addToast({
        type: 'success',
        title: 'Success',
        description: 'User deleted successfully',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to delete user',
      });
    }
  };

  const openEditDialog = (user: UserData) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status,
      department: user.department,
    });
    // Load custom permissions if they exist
    if (user.customPermissions && user.customPermissions.length > 0) {
      setUseCustomPermissions(true);
      setCustomPermissions(user.customPermissions);
    } else {
      setUseCustomPermissions(false);
      setCustomPermissions([]);
    }
    setShowEditDialog(true);
  };

  const openDeleteDialog = (user: UserData) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const openResetDialog = (user: UserData) => {
    setResetUser(user);
    setResetMode('auto');
    setResetPassword('');
    setResetConfirm('');
    setShowResetPassword(false);
    setGeneratedPassword(null);
    setCopied(false);
    setShowResetDialog(true);
  };

  const manualResetValid =
    resetMode === 'auto' ||
    (resetPassword.length >= 8 && resetPassword === resetConfirm);

  const handleResetPassword = async () => {
    if (!resetUser) return;
    if (resetMode === 'manual' && resetPassword.length < 8) {
      addToast({ type: 'error', title: 'Password too short', description: 'Use at least 8 characters.' });
      return;
    }
    if (resetMode === 'manual' && resetPassword !== resetConfirm) {
      addToast({ type: 'error', title: 'Passwords do not match', description: 'Re-enter the new password.' });
      return;
    }
    try {
      setResetting(true);
      const token = localStorage.getItem('accessToken');
      const body =
        resetMode === 'auto'
          ? { autoGenerate: true }
          : { password: resetPassword };
      const response = await fetch(`${API_BASE}/api/users/${resetUser.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to reset password');
      }
      const data = await response.json();
      if (data.temporaryPassword) {
        // Auto mode: reveal the generated password once for the admin to share.
        setGeneratedPassword(data.temporaryPassword);
        addToast({ type: 'success', title: 'Password reset', description: `Temporary password generated for ${resetUser.name}.` });
      } else {
        // Manual mode: nothing to reveal, close the dialog.
        setShowResetDialog(false);
        setResetUser(null);
        addToast({ type: 'success', title: 'Password reset', description: `${resetUser.name}'s password was updated.` });
      }
    } catch (error: any) {
      addToast({ type: 'error', title: 'Error', description: error.message || 'Failed to reset password' });
    } finally {
      setResetting(false);
    }
  };

  const copyGeneratedPassword = async () => {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      addToast({ type: 'error', title: 'Copy failed', description: 'Select and copy the password manually.' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'ABM_OPERATIONS',
      status: 'ACTIVE',
      department: '',
    });
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return 'Never';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <User className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-500">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard module="users" permission="read">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="User Management"
          description="Manage users, roles, and permissions"
          icon={Shield}
          actions={
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          }
        />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Users</p>
              <p className="text-2xl font-bold text-slate-700 mt-1">{users.length}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-100">
              <User className="w-6 h-6 text-slate-700" />
            </div>
          </div>
        </Card>
        <Card className="p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Active</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">
                {users.filter(u => u.status === 'ACTIVE').length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-100">
              <User className="w-6 h-6 text-emerald-700" />
            </div>
          </div>
        </Card>
        <Card className="p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Admins</p>
              <p className="text-2xl font-bold text-indigo-700 mt-1">
                {users.filter(u => u.role === 'ADMINISTRATOR').length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-indigo-100">
              <Shield className="w-6 h-6 text-indigo-700" />
            </div>
          </div>
        </Card>
        <Card className="p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">ABM Team</p>
              <p className="text-2xl font-bold text-blue-700 mt-1">
                {users.filter(u => u.role === 'ABM_MANAGER' || u.role === 'ABM_OPERATIONS').length}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-100">
              <User className="w-6 h-6 text-blue-700" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Roles</option>
            <option value="ADMINISTRATOR">Administrator</option>
            <option value="ABM_MANAGER">ABM Manager</option>
            <option value="ABM_OPERATIONS">ABM Operations</option>
            <option value="MDA">MDA - Manufacturing Discovery Analyst</option>
            <option value="SALES">Sales</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="SUSPENDED">Suspended</option>
          </select>

          {/* Results Count */}
          <div className="flex items-center text-sm text-gray-600">
            <Filter className="w-4 h-4 mr-2" />
            {filteredUsers.length} of {users.length} users
          </div>
        </div>
      </Card>

      {/* User Table */}
      <Card className="overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map(user => {
                const roleConfig = ROLE_CONFIG[user.role] || { label: user.role, color: 'bg-gray-100 text-gray-800 border-gray-200', icon: User };
                const RoleIcon = roleConfig.icon;
                return (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-700" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={`${roleConfig.color} border`}>
                        <RoleIcon className="w-3 h-3 mr-1" />
                        {roleConfig.label}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge className={`${(STATUS_CONFIG[user.status] || STATUS_CONFIG['INACTIVE']).color} border`}>
                        {(STATUS_CONFIG[user.status] || STATUS_CONFIG['INACTIVE']).label}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                        {user.department}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {formatDate(user.lastLogin)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          title="Edit user"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {canResetPasswords && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openResetDialog(user)}
                            title="Reset password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteDialog(user)}
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <User className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No users found matching your filters</p>
          </div>
        )}
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john.smith@cantier.com"
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="ABM_OPERATIONS">ABM Operations</option>
                <option value="ABM_MANAGER">ABM Manager</option>
                <option value="SALES">Sales</option>
                <option value="MDA">MDA</option>
                <option value="ADMINISTRATOR">Administrator</option>
              </select>
            </div>
            
            {/* Permission Matrix */}
            <div>
              <Label className="mb-3">Page Access Permissions</Label>
              <PermissionMatrix role={formData.role} readOnly />
            </div>
            
            <div>
              <Label>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="Engineering"
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="ABM_OPERATIONS">ABM Operations</option>
                <option value="ABM_MANAGER">ABM Manager</option>
                <option value="SALES">Sales</option>
                <option value="MDA">MDA</option>
                <option value="ADMINISTRATOR">Administrator</option>
              </select>
            </div>
            
            {/* Permission Configuration */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base font-semibold">Access Permissions</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {useCustomPermissions ? 'Custom Permissions' : 'Role-Based Permissions'}
                  </span>
                  <button
                    onClick={() => {
                      const enabling = !useCustomPermissions;
                      setUseCustomPermissions(enabling);
                      if (enabling) {
                        const existing = selectedUser?.customPermissions;
                        // Seed from existing overrides, else from the role's current defaults,
                        // so we never start from a deny-all matrix.
                        setCustomPermissions(
                          existing && existing.length > 0
                            ? existing
                            : roleDefaultsToCustom(formData.role)
                        );
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      useCustomPermissions ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      useCustomPermissions ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {useCustomPermissions ? (
                <div>
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>Custom Permissions:</strong> Click permissions below to enable/disable specific access for this user, overriding their role defaults.
                    </p>
                  </div>
                  <EditablePermissionMatrix
                    userPermissions={customPermissions}
                    onChange={setCustomPermissions}
                  />
                </div>
              ) : (
                <div>
                  <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <p className="text-sm text-gray-700">
                      <strong>Role-Based Permissions:</strong> This user inherits standard permissions for the <strong>{formData.role}</strong> role.
                    </p>
                  </div>
                  <PermissionMatrix role={formData.role} readOnly />
                </div>
              )}
            </div>
            
            <div>
              <Label>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? 
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
            >
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={(open) => {
        setShowResetDialog(open);
        if (!open) setResetUser(null);
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-slate-700" />
              Reset Password
            </DialogTitle>
          </DialogHeader>

          {generatedPassword ? (
            /* Success state — reveal the generated password once */
            <div className="space-y-4 py-2">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Copy this password now — it won't be shown again. Share it securely with{' '}
                  <strong>{resetUser?.name}</strong> and ask them to change it after signing in.
                </p>
              </div>
              <div>
                <Label>Temporary password</Label>
                <div className="flex gap-2 mt-1">
                  <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-mono text-sm break-all select-all">
                    {generatedPassword}
                  </code>
                  <Button variant="outline" size="sm" onClick={copyGeneratedPassword} title="Copy">
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setShowResetDialog(false); setResetUser(null); }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <p className="text-sm text-gray-600">
                Reset the password for <strong>{resetUser?.name}</strong>{' '}
                <span className="text-gray-400">({resetUser?.email})</span>.
              </p>

              {/* Mode selector */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setResetMode('auto')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                    resetMode === 'auto'
                      ? 'border-blue-500 bg-blue-50 text-blue-800'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  Generate for me
                </button>
                <button
                  type="button"
                  onClick={() => setResetMode('manual')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                    resetMode === 'manual'
                      ? 'border-blue-500 bg-blue-50 text-blue-800'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <KeyRound className="w-4 h-4" />
                  Set manually
                </button>
              </div>

              {resetMode === 'auto' ? (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-sm text-gray-700">
                    A strong temporary password will be generated and shown to you once so you can share it with the user.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label>New password</Label>
                    <div className="relative">
                      <Input
                        type={showResetPassword ? 'text' : 'password'}
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPassword(!showResetPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500 hover:text-gray-700"
                      >
                        {showResetPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {resetPassword.length > 0 && resetPassword.length < 8 && (
                      <p className="text-xs text-red-600 mt-1">Must be at least 8 characters.</p>
                    )}
                  </div>
                  <div>
                    <Label>Confirm new password</Label>
                    <Input
                      type={showResetPassword ? 'text' : 'password'}
                      value={resetConfirm}
                      onChange={(e) => setResetConfirm(e.target.value)}
                      placeholder="Re-enter password"
                    />
                    {resetConfirm.length > 0 && resetPassword !== resetConfirm && (
                      <p className="text-xs text-red-600 mt-1">Passwords don't match.</p>
                    )}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowResetDialog(false); setResetUser(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} disabled={resetting || !manualResetValid}>
                  {resetting ? 'Resetting…' : 'Reset Password'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </PermissionGuard>
  );
}
